import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, CheckCircle2, Package, FileText, Award } from 'lucide-react';

const Raisins = () => {
  const products = [
    {
      id: 'golden',
      name: 'Golden Raisins',
      origin: 'India, Turkey, USA',
      bestFor: 'Premium retail, bakery, confectionery, cereals',
      description: 'Light golden color, treated with sulfur dioxide for color retention',
      qualityFocus: 'Uniform golden color, plump texture, good moisture',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/79f7cbbe7d0eca54a949edc15ad34d6c14272d345fbb9f7c16711be7e768536d.png'
    },
    {
      id: 'sultana',
      name: 'Sultana Raisins',
      origin: 'Turkey, India',
      bestFor: 'Bakery, confectionery, trail mixes, cereals',
      description: 'Small, seedless, light to medium brown color',
      qualityFocus: 'Soft texture, sweet flavor, consistent sizing',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/79f7cbbe7d0eca54a949edc15ad34d6c14272d345fbb9f7c16711be7e768536d.png'
    },
    {
      id: 'thompson',
      name: 'Thompson Seedless',
      origin: 'USA, India',
      bestFor: 'Bulk ingredient, bakery, snack mixes',
      description: 'Dark brown natural sun-dried raisins',
      qualityFocus: 'Natural processing, good shelf stability',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/79f7cbbe7d0eca54a949edc15ad34d6c14272d345fbb9f7c16711be7e768536d.png'
    },
    {
      id: 'black',
      name: 'Black Raisins',
      origin: 'India, Afghanistan',
      bestFor: 'Traditional recipes, health foods, premium retail',
      description: 'Dark colored, from black grape varieties',
      qualityFocus: 'Rich flavor, soft texture, distinctive appearance',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/79f7cbbe7d0eca54a949edc15ad34d6c14272d345fbb9f7c16711be7e768536d.png'
    }
  ];

  const specifications = [
    { label: 'Moisture', value: '13-16% (grade dependent)' },
    { label: 'Sugar Content', value: '60-70%' },
    { label: 'Defects', value: '< 5% (premium grades lower)' },
    { label: 'Foreign Matter', value: '< 0.1%' }
  ];

  const grades = [
    {
      name: 'Standard Export',
      description: 'Best for bulk sourcing and value-led programs',
      features: ['Standard grading + batch COA', 'Aligned to buyer/destination specifications', 'Cost-effective bulk supply']
    },
    {
      name: 'Premium (EU/USA-Ready)',
      description: 'Best for private label and strict QC retail programs',
      features: ['Tighter QC + enhanced traceability', 'Premium packaging options', 'Expanded COA panel as required']
    }
  ];

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/products/dry-fruits" className="inline-flex items-center text-[#263776] hover:text-[#F47D20] mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dry Fruits
            </Link>
            <div className="text-center">
              <h1 className="text-5xl font-bold mb-4">Raisins</h1>
              <p className="text-xl text-slate-700 mb-6">
                Premium golden and dark raisins for bakery, confectionery, cereal, and snack applications
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm">
                <span className="bg-[#263776] text-white px-4 py-2 rounded-full">Multiple Varieties</span>
                <span className="bg-[#F47D20] text-white px-4 py-2 rounded-full">India · Turkey · USA</span>
                <span className="bg-slate-700 text-white px-4 py-2 rounded-full">Batch COA Always Supplied</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Grading Options */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8">Grading Options</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {grades.map((grade, index) => (
                <Card key={index} className="p-6 border-t-4 border-t-[#263776]">
                  <h3 className="text-xl font-bold text-[#263776] mb-2">{grade.name}</h3>
                  <p className="text-slate-600 mb-4">{grade.description}</p>
                  <ul className="space-y-2">
                    {grade.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <CheckCircle2 className="w-4 h-4 text-[#F47D20] mr-2 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Raisin Varieties */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Raisin Varieties</h2>
              <p className="text-lg text-slate-600">Premium varieties from multiple origins</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {products.map((product) => (
                <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover min-h-[200px]"
                    />
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-[#263776] mb-1">{product.name}</h3>
                      <p className="text-xs text-slate-500 mb-2">Origin: {product.origin}</p>
                      <p className="text-sm text-[#F47D20] font-medium mb-3">{product.bestFor}</p>
                      <p className="text-sm text-slate-600 mb-3">{product.description}</p>
                      <p className="text-xs text-slate-500 mb-4"><strong>Quality:</strong> {product.qualityFocus}</p>
                      
                      <Button asChild size="sm" className="w-full bg-[#263776] hover:bg-[#1e2a5e]">
                        <Link to={`/contact?product=raisins-${product.id}`}>Request Quote</Link>
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Specifications */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8">Typical Specifications</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {specifications.map((spec, index) => (
                <Card key={index} className="p-4 text-center">
                  <h3 className="font-bold text-[#263776] mb-1">{spec.label}</h3>
                  <p className="text-sm text-slate-600">{spec.value}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quality Baseline */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-[#263776] mb-6 text-center">SPANXPORT Quality Baseline</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#263776] rounded-full flex items-center justify-center mx-auto mb-3">
                    <Package className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-2">Processing Options</h3>
                  <p className="text-sm text-slate-600">Oil-coated and natural (uncoated) available</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#F47D20] rounded-full flex items-center justify-center mx-auto mb-3">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-2">Traceability</h3>
                  <p className="text-sm text-slate-600">Lot/production/date codes for full traceability</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-2">Testing</h3>
                  <p className="text-sm text-slate-600">Pesticide residue, SO2, and microbiological testing</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#263776] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Request a Quote for Raisins</h2>
          <p className="text-xl mb-4 max-w-2xl mx-auto">
            Share your requirements and we'll respond with offer + lead time + documentation list
          </p>
          <div className="flex gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#F47D20] text-white hover:bg-[#d66b1a]">
              <Link to="/contact">Request Detailed Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white bg-white/10 hover:bg-white/20">
              <Link to="/products/dry-fruits">Back to Dry Fruits</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Raisins;
